import { useCallback, useEffect, useRef, useState } from 'react';

export const SPELL_DAMAGE_FEEDBACK_EVENT = 'spell-damage:feedback';
export const SPELL_DAMAGE_ROLLED_EVENT = 'spell-damage:rolled';
export const SPELL_DAMAGE_LOG_EVENT = 'spell-damage:log';
const totalDice = evidence => Array.isArray(evidence) ? evidence.flat().reduce((sum, die) => sum + Number(die), 0) : undefined;

// Both instant resolutions and persistent triggers use the existing socket stream.
// This hook adds no reads, polling, or HP calculations.
export function feedbackFromResolution(resolution) {
  if (!resolution?.resolutionId || !resolution?.sourceCombatantId) return [];
  if (resolution.kind === 'area-cast') {
    const batch = { id: resolution.resolutionId, campaignId: resolution.campaignId, sourceCombatantId: resolution.sourceCombatantId,
      label: resolution.sourceEffect?.displayName || 'Spell', value: totalDice(resolution.damageEvidence),
      damageType: resolution.rules?.damage?.components?.map(item => item.type).join(' + '),
      targets: (resolution.targetCombatantIds || []).map((id, index) => ({ id: resolution.targetResolutionIds?.[index] || id,
        targetCombatantId: id, status: resolution.status === 'cancelled' ? 'cancelled' : 'waiting-save' })) };
    const children = resolution.targetResults?.flatMap(feedbackFromResolution) || [];
    return children.length ? [batch, ...children] : [batch];
  }
  return [{ id: resolution.parentResolutionId || resolution.resolutionId, campaignId: resolution.campaignId,
    sourceCombatantId: resolution.sourceCombatantId, label: resolution.sourceEffect?.displayName || 'Spell',
    value: totalDice(resolution.damageEvidence), damageType: resolution.rules?.damage?.components?.map(item => item.type).join(' + '),
    targets: [{ id: resolution.resolutionId, targetCombatantId: resolution.targetCombatantId, targetName: resolution.targetSave?.targetName,
      status: resolution.status === 'finalized' ? 'resolved' : resolution.status === 'cancelled' ? 'cancelled' : resolution.damageEvidence ? 'waiting-save' : 'waiting-damage',
      ...(resolution.status === 'finalized' ? { success: resolution.targetSave?.response?.succeeded, finalDamage: resolution.damageOutcome?.finalDamage, resistanceReduction: resolution.damageOutcome?.resistanceReduction,
        // The server's explanation (save roll, dice rolled, halving, traits, damage taken) for the roll log.
        ...(Array.isArray(resolution.combatLog?.lines) ? { logLines: resolution.combatLog.lines } : {}) } : {}) }] }];
}

// Changes whenever the popup's spell content does (new batch, damage total, or a target's save resolving).
export const spellFeedbackKey = feedback => (feedback ? `${feedback.id}:${feedback.value}:${(feedback.targets || []).map(target => target.status).join(',')}` : '');
// Whether a target is still waiting on the damage roll or its save; the popup stays up until they are in.
export const spellFeedbackPending = feedback => Boolean(feedback?.targets?.some(target => ['waiting-damage', 'waiting-save'].includes(target.status)));

export function mergeSpellFeedback(previous, incoming) {
  const targets = new Map((previous?.targets || []).map(item => [item.targetCombatantId, item]));
  const rank = { 'waiting-damage': 0, 'waiting-save': 1, cancelled: 2, resolved: 3 };
  for (const item of incoming.targets || []) {
    const old = targets.get(item.targetCombatantId);
    if (!old || rank[item.status] >= rank[old.status]) targets.set(item.targetCombatantId, { ...old, ...item });
  }
  return { ...previous, ...incoming, value: incoming.value ?? previous?.value, targets: [...targets.values()] };
}

export default function useSpellDamageFeedback({ campaignId, combatantId, acceptsSource, targetName, socket }) {
  const [batches, setBatches] = useState([]);
  const [log, setLog] = useState([]);
  const [dismissed, setDismissed] = useState(null);
  const completed = useRef(new Set());
  const seen = useRef(new Map());
  const scope = useRef(); scope.current = { campaignId, combatantId, acceptsSource, targetName };
  const accept = useCallback(payload => {
    const current = scope.current;
    const incoming = payload?.kind === 'persistent-save-feedback' ? payload.batches || [] : feedbackFromResolution(payload);
    for (const batch of incoming) {
      if (batch.campaignId !== current.campaignId || !(current.acceptsSource ? current.acceptsSource(batch.sourceCombatantId) : String(batch.sourceCombatantId) === String(current.combatantId))) continue;
      const previous = seen.current.get(batch.id);
      const merged = mergeSpellFeedback(previous, batch);
      if (JSON.stringify(previous) === JSON.stringify(merged)) continue;
      seen.current.set(batch.id, merged);
      if (seen.current.size > 24) seen.current.delete(seen.current.keys().next().value);
      setBatches(old => [...old.filter(item => item.id !== batch.id), merged].slice(-12));
      for (const target of merged.targets) if (target.status === 'resolved' && Number.isFinite(target.finalDamage) && !completed.current.has(target.id)) {
        completed.current.add(target.id);
        if (completed.current.size > 200) completed.current.delete(completed.current.values().next().value);
        setLog(old => [{ ...target, label: batch.label, damageType: batch.damageType }, ...old].slice(0, 50));
        window.dispatchEvent(new CustomEvent(SPELL_DAMAGE_LOG_EVENT, { detail: { campaignId: batch.campaignId,
          sourceCombatantId: batch.sourceCombatantId, label: batch.label, damageType: batch.damageType, ...target, targetName: target.targetName || current.targetName?.(target.targetCombatantId) || target.targetCombatantId } }));
      }
    }
    if (payload?.cancelledCastId && payload.campaignId === current.campaignId && (current.acceptsSource ? current.acceptsSource(payload.sourceCombatantId) : String(payload.sourceCombatantId) === String(current.combatantId))) {
      for (const [id, batch] of seen.current) if (batch.castId === payload.cancelledCastId) seen.current.set(id, { ...batch, targets: batch.targets.map(target => target.status === 'resolved' ? target : { ...target, status: 'cancelled' }) });
      setBatches(old => old.map(batch => batch.castId === payload.cancelledCastId ? { ...batch,
        targets: batch.targets.map(target => target.status === 'resolved' ? target : { ...target, status: 'cancelled' }) } : batch));
    }
  }, []);
  useEffect(() => {
    setBatches([]); setLog([]); setDismissed(null); completed.current.clear(); seen.current.clear();
  }, [campaignId, combatantId]);
  useEffect(() => {
    const handle = event => accept(event.detail);
    const clearForAttack = () => setBatches([]);
    window.addEventListener('damage-roll', clearForAttack);
    window.addEventListener(SPELL_DAMAGE_FEEDBACK_EVENT, handle);
    socket?.on?.('save-damage:update', accept);
    return () => { window.removeEventListener('damage-roll', clearForAttack); window.removeEventListener(SPELL_DAMAGE_FEEDBACK_EVENT, handle); socket?.off?.('save-damage:update', accept); };
  }, [accept, socket]);
  const candidates = [...batches].reverse().filter(batch => Number.isFinite(batch.value));
  // Recovered history must not hide a batch that is still waiting for saves.
  const latest = candidates.find(batch => batch.targets.some(target => ['waiting-damage', 'waiting-save'].includes(target.status))) || candidates[0];
  const feedback = latest?.id === dismissed ? null : latest || null;
  return { feedback, log, dismiss: () => setDismissed(feedback?.id) };
}
